import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import { AttrNumberInput, AttrTextInput } from '@/components/Inputs';
import type { SelectOptionT } from '@/components/Select/types';
import { AttrText, MainText } from '@/components/Typography/NamedTypo';
import PropertyValue from '@/components/Typography/PropertyValue';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useCompanyStore from '@/features/Account/store';
import {
  type COMPANY_ACTIVE_STATUS,
  COMPANY_STATUS_MAP,
} from '@/features/Account/types/constants';
import AgentDropdown from '@/features/Agent/components/AgentDropdown';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import useCompanyAttributes from '@/hooks/domain/useCompanyAttributes';
import { useInputs } from '@/hooks/pure/useInputs';
import type { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { readableFormat } from '@/utils/time';

interface Props {
  companyAlias: string;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
}

const CompanyInfoTab = ({ companyAlias }: Props) => {
  const changeCompanyAttr = useCompanyStore((state) => state.changeCompanyAttr);
  const companyMap = useCompanyStore((state) => state.companyMap);
  const setPopup = usePopupStore((state) => state.setPopup);
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);
  const updateWorkspaceFieldOptions = useWorkspaceStore(
    (state) => state.updateWorkspaceFieldOptions,
  );

  const company = useMemo(() => {
    return companyMap[companyAlias];
  }, [companyAlias, companyMap]);

  const {
    industryOptions,
    sizeOptions,
    planOptions,
    ratingOptions,
    accountTypeOptions,
    accountStageOptions,
    accountPrimaryUsecseOptions,
    countryOptions,
    statusOptions,
  } = useCompanyAttributes();

  const initInputs = useMemo(
    () => ({
      name: company?.name,
      employee_count:
        company?.employee_count !== undefined
          ? String(company.employee_count)
          : '',
      monthly_spend:
        company?.monthly_spend !== undefined
          ? String(company.monthly_spend)
          : '',
      annual_revenue:
        company?.annual_revenue !== undefined
          ? String(company.annual_revenue)
          : '',
      key: company?.key !== undefined ? String(company.key) : '',
    }),
    [
      company?.name,
      company?.employee_count,
      company?.monthly_spend,
      company?.annual_revenue,
      company?.key,
    ],
  );

  const [inputs, onChange, onCustomChange] = useInputs(initInputs);

  const handleOptionDelete = (
    option: SelectOptionT,
    target: WorkspaceConfigsFields,
    itemName: string,
  ) => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: `Delete the ${itemName}`,
      message: `Are you sure you want to delete this ${itemName}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => {
        updateWorkspaceFieldOptions(4, target, option.searchName);
      },
    });
  };

  const handleBlur = async (
    target: keyof CompanyT & keyof typeof inputs,
    isNumber = false,
  ) => {
    const origValue = company[target] !== undefined ? company[target] : null;
    let currValue: string | number | null = null;
    if (inputs[target] !== '') {
      if (isNumber) {
        currValue = Number(inputs[target]);
      } else {
        currValue = inputs[target];
      }
    }
    if (origValue !== currValue) {
      const data = await changeCompanyAttr(
        company.alias,
        target,
        currValue as string,
      );
      if (target === 'name' && !data) {
        addDefaultToast('This account name already exists.', 'fail');
        return;
      }
      if (target === 'key' && !data) {
        addDefaultToast('This account name already exists.', 'fail');
        return;
      }
    }
  };

  const onKeyEnter = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  useEffect(() => {
    onCustomChange('name', initInputs.name);
    onCustomChange('employee_count', initInputs.employee_count);
    onCustomChange('monthly_spend', initInputs.monthly_spend);
    onCustomChange('annual_revenue', initInputs.annual_revenue);
    onCustomChange('key', initInputs.key);
  }, [initInputs, onCustomChange]);

  if (!company) return null;

  return (
    <Style>
      <Flex className="company-property-area">
        <PropertyRow>
          <AttrText className="property-label">Industry</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-industry-dropdown`}
              view="detail"
              selected={company.industry || null}
              options={industryOptions}
              onSelect={(industry) =>
                changeCompanyAttr(company.alias, 'industry', industry)
              }
              onCreate={async (industry) => {
                await updateWorkspaceFieldOptions(1, 'industry', industry);
                changeCompanyAttr(
                  company.alias,
                  'industry',
                  industry,
                );
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'industry', 'Industry item')
              }
              onClear={() => changeCompanyAttr(company.alias, 'industry', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Employees</AttrText>
          <AttrNumberInput
            name={'employee_count'}
            value={inputs.employee_count}
            placeholder="Number of employees"
            onChange={onChange}
            onKeyDown={onKeyEnter}
            onBlur={() => handleBlur('employee_count', true)}
          />
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Size</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-size-dropdown`}
              view="detail"
              selected={company.size || null}
              options={sizeOptions}
              onSelect={(size) =>
                changeCompanyAttr(company.alias, 'size', size)
              }
              onCreate={async (size) => {
                await updateWorkspaceFieldOptions(1, 'size', size);
                changeCompanyAttr(company.alias, 'size', size);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'size', 'Size item')
              }
              onClear={() => changeCompanyAttr(company.alias, 'size', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Monthly spend</AttrText>
          <AttrNumberInput
            name={'monthly_spend'}
            value={inputs.monthly_spend}
            placeholder="Account's monthly subscription amount"
            onChange={onChange}
            onKeyDown={onKeyEnter}
            onBlur={() => handleBlur('monthly_spend', true)}
          />
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Annual revenue</AttrText>
          <AttrNumberInput
            name={'annual_revenue'}
            value={inputs.annual_revenue}
            placeholder="Account's annual revenue amount"
            onChange={onChange}
            onKeyDown={onKeyEnter}
            onBlur={() => handleBlur('annual_revenue', true)}
          />
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Plan</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-plan-dropdown`}
              view="detail"
              selected={company.plan || null}
              options={planOptions}
              onSelect={(plan) =>
                changeCompanyAttr(company.alias, 'plan', plan)
              }
              onCreate={async (plan) => {
                await updateWorkspaceFieldOptions(1, 'plan', plan);
                changeCompanyAttr(company.alias, 'plan', plan);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'plan', 'Plan item')
              }
              onClear={() => changeCompanyAttr(company.alias, 'plan', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Rating</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-rating-dropdown`}
              view="detail"
              selected={company.rating || null}
              options={ratingOptions}
              onSelect={(rating) =>
                changeCompanyAttr(company.alias, 'rating', rating)
              }
              onCreate={async (rating) => {
                await updateWorkspaceFieldOptions(1, 'rating', rating);
                changeCompanyAttr(company.alias, 'rating', rating);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'rating', 'Rating item')
              }
              onClear={() => changeCompanyAttr(company.alias, 'rating', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Type</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-type-dropdown`}
              view="detail"
              selected={company.type || null}
              options={accountTypeOptions}
              onSelect={(type) =>
                changeCompanyAttr(company.alias, 'type', type)
              }
              onCreate={async (type) => {
                await updateWorkspaceFieldOptions(1, 'account_type', type);
                changeCompanyAttr(company.alias, 'type', type);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'account_type', 'Type item')
              }
              onClear={() => changeCompanyAttr(company.alias, 'type', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Stage</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-stage-dropdown`}
              view="detail"
              selected={company.stage || null}
              options={accountStageOptions}
              onSelect={(stage) =>
                changeCompanyAttr(company.alias, 'stage', stage)
              }
              onCreate={async (stage) => {
                await updateWorkspaceFieldOptions(1, 'account_stage', stage);
                changeCompanyAttr(company.alias, 'stage', stage);
              }}
              onDelete={(option) =>
                handleOptionDelete(option, 'account_stage', 'Stage item')
              }
              onClear={() => changeCompanyAttr(company.alias, 'stage', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Primary Usecase</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-usecase-dropdown`}
              view="detail"
              selected={company.primary_usecase || null}
              options={accountPrimaryUsecseOptions}
              onSelect={(usecase) =>
                changeCompanyAttr(
                  company.alias,
                  'primary_usecase',
                  usecase,
                )
              }
              onCreate={async (usecase) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'account_primary_usecase',
                  usecase,
                );
                changeCompanyAttr(
                  company.alias,
                  'primary_usecase',
                  usecase,
                );
              }}
              onDelete={(option) =>
                handleOptionDelete(
                  option,
                  'account_primary_usecase',
                  'Primary Usecase item',
                )
              }
              onClear={() =>
                changeCompanyAttr(company.alias, 'primary_usecase', null)
              }
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Country</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-country-dropdown`}
              view="detail"
              selected={company.country || null}
              options={countryOptions}
              onSelect={(country) =>
                changeCompanyAttr(company.alias, 'country', country)
              }
              onClear={() => changeCompanyAttr(company.alias, 'country', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">NPS Score</AttrText>
          <PropertyValue
            readOnly
            value={
              typeof company.nps === 'number' ? (
                company.nps
              ) : (
                <MainText dim>-</MainText>
              )
            }
          />
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">CES Score</AttrText>
          <PropertyValue
            readOnly
            value={
              typeof company.ces === 'number' ? (
                company.ces
              ) : (
                <MainText dim>-</MainText>
              )
            }
          />
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">CSAT Score</AttrText>
          <PropertyValue
            readOnly
            value={
              typeof company.csat === 'number' ? (
                company.csat
              ) : (
                <MainText dim>-</MainText>
              )
            }
          />
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Feedback</AttrText>
          <PropertyValue
            readOnly
            value={
              company.feedback_count !== undefined
                ? String(company.feedback_count)
                : '0'
            }
          />
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Active status</AttrText>
          <SelectValueWrapper>
            <AttrSelectDropdown
              name={`company-detail-${company.alias}-status-dropdown`}
              view="detail"
              selected={
                (company.status && COMPANY_STATUS_MAP.get(company.status)) ||
                null
              }
              options={statusOptions}
              onSelect={(status) =>
                changeCompanyAttr(
                  company.alias,
                  'status',
                  Number(status) as COMPANY_ACTIVE_STATUS,
                )
              }
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Created date</AttrText>
          {company.created_at && (
            <PropertyValue
              readOnly
              value={readableFormat(company.created_at)}
            />
          )}
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Last activity</AttrText>
          {company.latest_activity && (
            <PropertyValue
              readOnly
              value={readableFormat(company.latest_activity)}
            />
          )}
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Agent</AttrText>
          <SelectValueWrapper>
            <AgentDropdown
              selected={company.agent || NOT_ASSIGNED_LABEL}
              name={`company-detail-${company.alias}-agent-dropdown`}
              onSelect={(agent) =>
                changeCompanyAttr(company.alias, 'agent', agent)
              }
              onClear={() => changeCompanyAttr(company.alias, 'agent', null)}
            />
          </SelectValueWrapper>
        </PropertyRow>
        <PropertyRow>
          <AttrText className="property-label">Key</AttrText>
          <AttrTextInput
            name={'key'}
            value={inputs.key}
            placeholder="Account's key"
            onChange={onChange}
            onKeyDown={onKeyEnter}
            onBlur={() => handleBlur('key', false)}
          />
        </PropertyRow>
      </Flex>
      <Space h="22px" />
    </Style>
  );
};

export default CompanyInfoTab;

const Style = styled.div`
  .company-property-area {
    flex-direction: column;
    gap: 5px;
    .property-label {
      width: 120px;
    }
  }
`;

const PropertyRow = styled(Flex).attrs({
  g: '10px',
  a: 'center',
})`
  height: 32px;
`;

const SelectValueWrapper = styled(Flex)`
  width: calc(100% - 100px);
`;
